import * as React from "react";
import * as SwitchPrimitive from "@radix-ui/react-switch";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { Check, Copy, CircleCheck, CircleX, LoaderCircle, Ban, Clock } from "lucide-react";
import { cn } from "./utils";
export type GenStatus = "queued" | "running" | "succeeded" | "failed" | "canceled";

/* ---------- Switch ---------- */
export const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root>
>(({ className, ...props }, ref) => (
  <SwitchPrimitive.Root
    ref={ref}
    className={cn(
      "peer inline-flex h-[18px] w-[30px] shrink-0 cursor-pointer items-center rounded-full border border-line-strong bg-hover p-px transition-colors duration-150",
      "data-[state=checked]:border-ink data-[state=checked]:bg-ink disabled:cursor-not-allowed disabled:opacity-50",
      className,
    )}
    {...props}
  >
    <SwitchPrimitive.Thumb className="pointer-events-none block size-3.5 rounded-full bg-raised shadow-[0_1px_2px_oklch(0.2_0.01_15/0.2)] transition-transform duration-150 ease-[var(--ease-out-quart)] data-[state=checked]:translate-x-3 data-[state=checked]:bg-canvas" />
  </SwitchPrimitive.Root>
));
Switch.displayName = "Switch";

/* ---------- Tabs (underline) ---------- */
export const Tabs = TabsPrimitive.Root;
export const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List ref={ref} className={cn("flex items-center gap-4 border-b border-line", className)} {...props} />
));
TabsList.displayName = "TabsList";
export const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      "-mb-px h-9 border-b border-transparent text-sm text-ink-3 transition-colors duration-150 hover:text-ink data-[state=active]:border-ink data-[state=active]:text-ink",
      className,
    )}
    {...props}
  />
));
TabsTrigger.displayName = "TabsTrigger";
export const TabsContent = TabsPrimitive.Content;

/* ---------- Segmented control ---------- */
export function Segmented<T extends string>({
  value,
  onChange,
  options,
  label,
  size = "md",
  className,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: React.ReactNode; disabled?: boolean }[];
  label: string;
  size?: "sm" | "md";
  className?: string;
}) {
  return (
    <div role="radiogroup" aria-label={label} className={cn("inline-flex gap-0.5 rounded-sm border border-line bg-hover p-0.5", className)}>
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={on}
            disabled={o.disabled}
            onClick={() => onChange(o.value)}
            className={cn(
              "inline-flex shrink-0 items-center gap-1.5 rounded-[4px] px-2.5 font-medium whitespace-nowrap transition-colors duration-150 outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:opacity-40",
              size === "sm" ? "h-6 text-xs" : "h-7 text-sm",
              on ? "bg-raised text-ink shadow-[var(--shadow-tile)]" : "text-ink-3 hover:bg-raised/60 hover:text-ink",
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/* ---------- Chip (neutral) ---------- */
export function Chip({ children, className, mono }: { children: React.ReactNode; className?: string; mono?: boolean }) {
  return (
    <span
      className={cn(
        "inline-flex h-5 items-center rounded-[4px] border border-line px-1.5 text-2xs font-medium whitespace-nowrap text-ink-2",
        mono && "font-mono font-normal",
        className,
      )}
    >
      {children}
    </span>
  );
}

/* ---------- Status (colour + icon + text, never colour alone) ---------- */
const STATUS: Record<GenStatus, { label: string; icon: React.ElementType; cls: string }> = {
  succeeded: { label: "Succeeded", icon: CircleCheck, cls: "text-ok" },
  failed: { label: "Failed", icon: CircleX, cls: "text-err" },
  running: { label: "Running", icon: LoaderCircle, cls: "text-info" },
  queued: { label: "Queued", icon: Clock, cls: "text-ink-3" },
  canceled: { label: "Canceled", icon: Ban, cls: "text-ink-3" },
};
export function Status({ status, className }: { status: GenStatus; className?: string }) {
  const s = STATUS[status];
  const Icon = s.icon;
  return (
    <span className={cn("inline-flex items-center gap-1.5 text-sm whitespace-nowrap text-ink", className)}>
      <Icon className={cn("size-3.5", s.cls, status === "running" && "animate-spin [animation-duration:2.4s]")} aria-hidden />
      {s.label}
    </span>
  );
}

export function Dot({ tone }: { tone: "ok" | "warn" | "err" | "muted" }) {
  return (
    <span
      aria-hidden
      className={cn(
        "inline-block size-1.5 shrink-0 rounded-full",
        tone === "ok" && "bg-ok",
        tone === "warn" && "bg-warn",
        tone === "err" && "bg-err",
        tone === "muted" && "bg-ink-4",
      )}
    />
  );
}


/* ---------- Kbd ---------- */
export function Kbd({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <kbd className={cn("inline-flex h-5 min-w-5 items-center justify-center rounded-[4px] border border-line bg-surface px-1 font-mono text-2xs text-ink-3", className)}>
      {children}
    </kbd>
  );
}

/* ---------- Copy button with inline confirmation ---------- */
export function CopyButton({ value, label = "Copy", className, showLabel }: { value: string; label?: string; className?: string; showLabel?: boolean }) {
  const [copied, setCopied] = React.useState(false);
  React.useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1600);
    return () => clearTimeout(t);
  }, [copied]);
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard?.writeText(value).catch(() => {});
        setCopied(true);
      }}
      aria-label={copied ? "Copied" : label}
      className={cn(
        "inline-flex h-7 items-center gap-1.5 rounded-sm px-1.5 text-xs text-ink-3 transition-colors duration-150 hover:bg-hover hover:text-ink",
        className,
      )}
    >
      {copied ? <Check className="size-3.5 text-ok" aria-hidden /> : <Copy className="size-3.5" aria-hidden />}
      {(showLabel || copied) && <span aria-live="polite">{copied ? "Copied" : label}</span>}
    </button>
  );
}

/* ---------- Tooltip ---------- */
export const TooltipProvider = TooltipPrimitive.Provider;
export function Tip({ content, children }: { content: React.ReactNode; children: React.ReactNode }) {
  return (
    <TooltipPrimitive.Root delayDuration={300}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          sideOffset={6}
          className="z-50 rounded-sm bg-ink px-2 py-1 text-xs text-canvas shadow-float"
        >
          {content}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}

/* ---------- Field ---------- */
export const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(({ className, ...props }, ref) => (
  <input
    ref={ref}
    className={cn(
      "h-8 w-full rounded-sm border border-line-strong bg-raised px-2.5 text-sm text-ink placeholder:text-ink-3 transition-colors duration-150",
      "hover:border-ink-4 focus-visible:border-focus focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[color-mix(in_oklch,var(--focus)_22%,transparent)]",
      "aria-[invalid=true]:border-err",
      className,
    )}
    {...props}
  />
));
Input.displayName = "Input";

export function Label({ children, htmlFor, className }: { children: React.ReactNode; htmlFor?: string; className?: string }) {
  return (
    <label htmlFor={htmlFor} className={cn("text-xs font-medium text-ink-2", className)}>
      {children}
    </label>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div aria-hidden className={cn("skeleton h-3", className)} />;
}
